import React, { useState, useEffect } from "react";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Form from "react-bootstrap/Form";
import Image from "react-bootstrap/Image";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import MainWrapper from "../layouts/MainWrapper";
import CardWrapper from "../layouts/CardWrapper";
import Button from "react-bootstrap/Button";
import PageTitle from "../components/PageTitle";
import CustomBreadcrumb from "../components/CustomBreadcrumb";
import { getAllCountries, getCitiesofSelectedState, getStatesofSelectedCountry } from "../services/countryStateService";
import Select from 'react-select';
import { clientRegister } from "../services/authService";
import { useAlert } from "../contexts/AlertContext";
import { useNavigate } from "react-router-dom";

const countryOptions = [
    { value: "US", label: "United States", code: "us" },
    { value: "IN", label: "India", code: "in" },
    { value: "GB", label: "United Kingdom", code: "gb" },
    { value: "FR", label: "France", code: "fr" },
];

const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone is required"),
    password: Yup.string()
        .min(8, "Minimum 8 characters")
        .matches(/[a-z]/, "At least one lowercase letter")
        .matches(/[A-Z]/, "At least one uppercase letter")
        .matches(/\d/, "At least one number")
        .matches(/[@$!%*?&#]/, "At least one special character")
        .required("Password is required"),
    confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    streetName: Yup.string().required("Street name is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    zipCode: Yup.string().required("Zip code is required"),
    companyName: Yup.string().required("CompanyName is required")
});

const ClientRegister = () => {
    const [loading, setLoading] = useState(true);
    const [phone, setPhone] = useState("");
    const [phoneDetails, setPhoneDetails] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);

    const [initialValues] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirm_password: "",
        streetName: "",
        country: countryOptions[0].value,
        state: "",
        city: "",
        zipCode: "",
        companyName: "",

    });
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        getAllCountries().then((response) => {
            if (response) {
                const options = response.map(c => ({
                    label: c.name,
                    value: c.isoCode
                }));
                setCountryList(options);
            }
        });
    }, []);

    useEffect(() => {
        if (!selectedCountry) {
            setStateList([]);
            setSelectedState(null);
            return;
        }
        getStatesofSelectedCountry(selectedCountry.value).then((response) => {
            if (response) {
                const options = response.map(s => ({
                    label: s.name,
                    value: s.isoCode
                }));
                setStateList(options);
            }
        });
        setSelectedState(null);
        setSelectedCity(null);
        setCityList([]);
    }, [selectedCountry]);
    useEffect(() => {
        if (!selectedState) {
            setCityList([]);
            setSelectedCity(null);
            return;
        }
        getCitiesofSelectedState(selectedCountry.value, selectedState.value).then((response) => {
            if (response) {
                const options = response.map(c => ({
                    label: c.name,
                    value: c.name
                }));
                setCityList(options);
            }
        });
        setSelectedCity(null);
    }, [selectedState]);

    const handleSelectChange = (type, option, setFieldValue) => {
        if (type === "country") {
            setSelectedCountry(option);
            setFieldValue("country", option?.value || "");
        } else if (type === "state") {
            setSelectedState(option);
            setFieldValue("state", option?.value || "");
        } else if (type === "city") {
            setSelectedCity(option);
            setFieldValue("city", option?.value || "");
        }
    };


    const PasswordToggle = (e) => {
        e.preventDefault();
        setShowPassword((prevState) => !prevState);
    };
    const ConfirmPasswordToggle = (e) => {
        e.preventDefault();
        setShowConfirmPassword((prevState) => !prevState);
    };
    const changePhoneNumber = (value) => {
        if (typeof value !== "undefined") {
            const phoneDetailsTemp = parsePhoneNumber(value);
            setPhoneDetails(phoneDetailsTemp);
            setPhone(value);
        }
    };

    const handleSubmit = async (values) => {
        const userToSave = {
            ...values,
            referral: '123123',
            country: selectedCountry.label,
            first_name: values.firstName,
            last_name: values.lastName,
            phone_no: phoneDetails?.nationalNumber,
            address: values.streetName,
            country_code: phoneDetails?.countryCallingCode,
        };
        try {
            await clientRegister(userToSave).then((res) => {
                showAlert({
                    title: "Users Notification",
                    message: res?.message ?? "Status updated successfully",
                    variant: "success",
                });
            })
            navigate('/client-access')
            console.log("User created successfully");
        } catch (error) {
            showAlert({
                title: "Error",
                message: error?.response?.data?.message ?? "something went wrong",
                variant: "danger",
            });
            console.error("Error saving user:", error);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <>
            <Row className="align-items-end justify-content-between mb-4">
                <Col lg="7">
                    <CustomBreadcrumb
                        items={[{ label: "Contacts", href: "/client-access" }, { label: "Register New Contact", active: true }]}
                    />
                    <PageTitle title="Register Contact" />
                </Col>
            </Row>

            <MainWrapper>
                <CardWrapper className="p-0">
                    <Formik
                        initialValues={initialValues}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ setFieldValue, handleChange, errors, values }) => (

                            <FormikForm>
                                <div className="input-group-warpper p-4 mt-3">
                                    <div className="input-group-item">
                                        <h2 className="fs-5 fw-semibold text-gray-800 mb-4">Personal Details</h2>

                                        <Row className="gy-4">

                                            <Form.Group className="col-md-6" controlId="firstName">
                                                <Form.Label className="required form-label">First Name</Form.Label>
                                                <Field name="firstName" className="form-control" placeholder="Enter first name" />
                                                <ErrorMessage name="firstName" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6" controlId="lastName">
                                                <Form.Label className="required form-label">Last Name</Form.Label>
                                                <Field name="lastName" className="form-control" placeholder="Enter last name" />
                                                <ErrorMessage name="lastName" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6" controlId="email">
                                                <Form.Label className="required form-label">Email</Form.Label>
                                                <Field name="email" type="email" className="form-control" placeholder="Enter email" />
                                                <ErrorMessage name="email" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6" controlId="phone">
                                                <Form.Label className="required form-label">Phone</Form.Label>
                                                <PhoneInput
                                                    className="form-control"
                                                    international
                                                    value={phone}
                                                    onChange={(e) => {
                                                        if (typeof e !== "undefined") {
                                                            changePhoneNumber(e);
                                                            setFieldValue("phone", e);
                                                        }
                                                    }}
                                                    placeholder="Enter phone number"
                                                />
                                                <ErrorMessage name="phone" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">Password</Form.Label>
                                                <div className="input-warpper lock-icon">
                                                    <Form.Control
                                                        type={showPassword ? "text" : "password"}
                                                        placeholder="Enter password"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        autoComplete="new-password"
                                                        isInvalid={!!errors.password}
                                                    />
                                                    <button type="button" className="input-group-icon btn-reset right" onClick={PasswordToggle}>
                                                        <Image src={showPassword ? "/images/eye-off.svg" : "/images/eye.svg"} alt="Eye Icon" />
                                                    </button>
                                                    <ErrorMessage name="password" component="div" className="text-danger" />
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">Confirm Password</Form.Label>
                                                <div className="input-warpper lock-icon">
                                                    <Form.Control
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        placeholder="Enter password"
                                                        name="confirm_password"
                                                        value={values.confirm_password}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.confirm_password}
                                                    />
                                                    <button type="button" className="input-group-icon btn-reset right" onClick={ConfirmPasswordToggle}>
                                                        <Image src={showConfirmPassword ? "/images/eye-off.svg" : "/images/eye.svg"} alt="Eye Icon" />
                                                    </button>
                                                    <ErrorMessage name="confirm_password" component="div" className="text-danger" />
                                                </div>
                                            </Form.Group>
                                        </Row>
                                    </div>
                                </div>

                                <div className="input-group-warpper p-4 mt-3">
                                    <div className="input-group-item">
                                        <h2 className="fs-5 fw-semibold text-gray-800 mb-4">Company Information</h2>

                                        <Row className="gy-4">
                                            <Form.Group className="col-md-6" controlId="CompanyName">
                                                <Form.Label className="required form-label">Company Name</Form.Label>
                                                <Field name="companyName" className="form-control" placeholder="Enter Company name" />
                                                <ErrorMessage name="companyName" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">Street Name</Form.Label>
                                                <Field name="streetName" className="form-control" placeholder="Enter street name" />
                                                <ErrorMessage name="streetName" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">Country</Form.Label>
                                                <Select
                                                    options={countryList}
                                                    value={selectedCountry}
                                                    onChange={(option) => handleSelectChange("country", option, setFieldValue)}
                                                    placeholder="Select Country"
                                                />
                                                <Field type="hidden" name="country" value={selectedCountry?.value || ""} />
                                                <ErrorMessage name="country" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">State</Form.Label>
                                                <Select
                                                    options={stateList}
                                                    value={selectedState}
                                                    onChange={(option) => handleSelectChange("state", option, setFieldValue)}
                                                    placeholder="Select State"
                                                    isDisabled={!selectedCountry}
                                                />
                                                <Field type="hidden" name="state" value={selectedState?.value || ""} />
                                                <ErrorMessage name="state" component="div" className="text-danger" />
                                            </Form.Group>

                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">City</Form.Label>
                                                <Select
                                                    options={cityList}
                                                    value={selectedCity}
                                                    onChange={(option) => handleSelectChange("city", option, setFieldValue)}
                                                    placeholder="Select City"
                                                    isDisabled={!selectedState}
                                                />
                                                <Field type="hidden" name="city" value={selectedCity?.value || ""} />
                                                <ErrorMessage name="city" component="div" className="text-danger" />
                                            </Form.Group>


                                            <Form.Group className="col-md-6">
                                                <Form.Label className="required form-label">Zip Code</Form.Label>
                                                <Field name="zipCode" className="form-control" placeholder="Enter zip code" />
                                                <ErrorMessage name="zipCode" component="div" className="text-danger" />
                                            </Form.Group>

                                        </Row>
                                    </div>
                                </div>

                                <div className="input-group-warpper d-flex gap-2 p-4 mt-3">
                                    <Button variant="primary" className="btn-md" type="submit">
                                        Save & Register Contact
                                    </Button>
                                    <Button className="btn-md" variant="outline-secondary" type="reset" onClick={() => navigate('/client-access')}>
                                        Cancel
                                    </Button>
                                </div>

                            </FormikForm>
                        )}
                    </Formik>
                </CardWrapper>
            </MainWrapper>
        </>
    );
};

export default ClientRegister;
